import { Lock } from 'lucide-react';
import { usePlanFeatures } from '@/hooks/use-plan-features';
import { toast } from '@/hooks/use-toast';
import type { FeatureSlug } from '@/config/features';

/**
 * Gating por TIPO de lançamento dentro de um `<Select>`: os tipos que o plano
 * não inclui continuam APARECENDO (com cadeado), mas não podem ser selecionados.
 *
 * Por que interceptar no `onValueChange` e não usar `disabled`: o Radix aplica
 * `data-[disabled]:pointer-events-none` no `SelectItem`, então um item `disabled`
 * engole o clique e nunca dispara o handler — não conseguiríamos mostrar a
 * mensagem. Mantemos o item habilitado e barramos a troca aqui.
 *
 * @param typeFeatureMap mapa valor-do-tipo -> slug que o libera (tipos ausentes
 *   são sempre liberados, ex.: `single`).
 * @param lockedMessage texto exibido no toast ao tentar um tipo bloqueado.
 */
export function useGatedTypeSelect(
  typeFeatureMap: Partial<Record<string, FeatureSlug>>,
  lockedMessage: string,
) {
  const { hasFeature } = usePlanFeatures();

  const isLocked = (value: string): boolean => {
    const slug = typeFeatureMap[value];
    return slug != null && !hasFeature(slug);
  };

  /**
   * Envolve o setter do form: se o valor escolhido estiver bloqueado, mostra o
   * toast e NÃO altera o valor; caso contrário, repassa para `setValue`.
   */
  const guardChange =
    <T extends string>(setValue: (value: T) => void) =>
    (value: T): void => {
      if (isLocked(value)) {
        toast({ variant: 'destructive', title: 'Recurso bloqueado', description: lockedMessage });
        return;
      }
      setValue(value);
    };

  /**
   * Conteúdo do `<SelectItem>`: label + cadeado quando bloqueado. O `title`
   * informa, ao passar o mouse, que o tipo não está disponível no plano.
   */
  const itemContent = (value: string, label: React.ReactNode): React.ReactNode =>
    isLocked(value) ? (
      <span className="flex items-center gap-2 opacity-60" title={lockedMessage}>
        {label}
        <Lock className="h-3.5 w-3.5" />
      </span>
    ) : (
      label
    );

  return { isLocked, guardChange, itemContent };
}
